// src/components/MealPlan/AlternativeRecipes.js
import { fetchAlternativeRecipes } from '../../services/apiService';
import { smartRecalculateGroceryList } from '../../utils/smartRecalculation';
import backendClient from '../../services/backendClient';
import { getFingerprint } from '../../utils/fingerprint';
import { PromptBuilder } from '../../utils/PromptBuilder';
import { getAlternativeNamingHint, PRODUCT_FORM_VALUES } from '../../utils/ingredientNamingRules';
import { getSupermarketReadyRule, getIngredientCompletenessRule, getProportionalityRuleAlternatives, getIngredientQualitySelfCheckCompact, getCookingComplexityRuleAlternatives, getNoBeveragesRule } from '../../utils/ingredientQualityRules';
import { getCurrentLanguage } from '../../services/i18n';

// Custom error class for insufficient credits/tries
export class InsufficientCreditsError extends Error {
  constructor(message = 'Not enough tries') {
    super(message);
    this.name = 'InsufficientCreditsError';
  }
}

// Custom error class for no tries remaining
export class NoTriesRemainingError extends Error {
  constructor(message = 'No tries remaining') {
    super(message);
    this.name = 'NoTriesRemainingError';
  }
}

// In-memory cache for alternative recipes
// Key: `${originalRecipeTitle}__${mealType}` (the original recipe that was first disliked in that slot)
// Value: { originalRecipeTitle, alternatives: [...], generatedFor: recipeTitle }
const alternativesCache = new Map();
// Tracks every recipe title that has appeared in each slot across all rounds of dislike/replace.
// Prevents a recipe from being re-suggested after the user has already seen/rejected it.
const slotHistory = new Map(); // key → Set<string (lowercase title)>

/**
 * Get cache key for a recipe slot based on meal type and position
 * We use a combination that identifies the "slot" in the meal plan
 * @param {Object} recipe - The recipe object
 * @param {Array} currentMealPlan - Current meal plan for position context
 * @returns {string} Cache key
 */
const getCacheKey = (recipe, currentMealPlan = []) => {
  const mealType = recipe.mealType || 'Unknown';
  // Find position among recipes of same meal type
  const sameTypeMeals = currentMealPlan.filter(r => r.mealType === mealType);
  const position = sameTypeMeals.findIndex(r => r.title === recipe.title);
  return `${mealType}__${position >= 0 ? position : 0}`;
};

/**
 * Check if a recipe slot can have alternatives generated
 * Now always returns true - credit check is handled in MealPlan.js
 * @param {Object} recipe - The recipe to check
 * @param {Array} currentMealPlan - Current meal plan for context
 * @returns {boolean} Whether alternatives can be shown for this slot
 */
export const canGetAlternatives = (recipe, currentMealPlan = []) => {
  // Credit-based limiting is now handled in MealPlan.js handleDislikeRecipe
  // This function now only checks if we have cached alternatives
  return true;
};

/**
 * Get the number of remaining alternative slots available
 * @deprecated Slot limiting is now handled by credit system in MealPlan.js
 * @returns {number} Always returns a high number since credits handle limiting
 */
export const getRemainingAlternativeSlots = () => {
  return 999; // No slot limit - credits handle limiting
};

/**
 * Get the count of used alternative slots
 * @returns {number} Number of slots used
 */
export const getUsedAlternativeSlots = () => {
  return alternativesCache.size;
};

/**
 * Get cached alternatives for a recipe, filtering out the current recipe
 * @param {Object} recipe - The current recipe being disliked
 * @param {Array} currentMealPlan - Current meal plan for context
 * @returns {Array|null} Cached alternatives (filtered) or null if not cached
 */
export const getCachedAlternatives = (recipe, currentMealPlan = []) => {
  const cacheKey = getCacheKey(recipe, currentMealPlan);
  const cached = alternativesCache.get(cacheKey);
  
  if (!cached) return null;
  
  // Filter out the current recipe from the cached alternatives
  const filteredAlternatives = cached.alternatives.filter(
    alt => alt.title.toLowerCase() !== recipe.title.toLowerCase()
  );
  
  // If no alternatives left after filtering, return null to trigger new generation
  if (filteredAlternatives.length === 0) return null;
  
  console.log(`📦 Using cached alternatives for slot ${cacheKey}:`, filteredAlternatives.map(a => a.title));
  return filteredAlternatives;
};

/**
 * Store alternatives in cache for a recipe slot
 * @param {Object} recipe - The recipe that was disliked
 * @param {Array} alternatives - The generated alternatives
 * @param {Array} currentMealPlan - Current meal plan for context
 */
export const cacheAlternatives = (recipe, alternatives, currentMealPlan = []) => {
  const cacheKey = getCacheKey(recipe, currentMealPlan);
  alternativesCache.set(cacheKey, {
    originalRecipeTitle: recipe.title,
    alternatives: alternatives,
    generatedFor: recipe.title,
    cachedAt: Date.now()
  });
  // Record all titles that have ever appeared in this slot so they can be
  // excluded from future alternative generations after multiple replacements.
  if (!slotHistory.has(cacheKey)) slotHistory.set(cacheKey, new Set());
  const hist = slotHistory.get(cacheKey);
  hist.add(recipe.title.toLowerCase());
  alternatives.forEach(a => hist.add(a.title.toLowerCase()));
  console.log(`💾 Cached alternatives for slot ${cacheKey}:`, alternatives.map(a => a.title));
};

/**
 * Clear all cached alternatives (call when meal plan is regenerated)
 */
export const clearAlternativesCache = () => {
  alternativesCache.clear();
  slotHistory.clear();
  console.log('🧹 Cleared alternatives cache');
};

/**
 * Verify the user has at least 1 replacement try, then consume one BEFORE the AI
 * request (prevents abuse). Used by generateAlternativeRecipesAsync.
 *
 * @throws {NoTriesRemainingError} when no replacements are left
 * @throws {InsufficientCreditsError} when the credit check itself fails
 */
export const checkAndConsumeReplacementTry = async () => {
  // Check if user has enough credits (at least 1 try required for replace)
  try {
    const fingerprint = getFingerprint();
    const queryParams = fingerprint ? `?fingerprint=${encodeURIComponent(fingerprint)}` : '';
    const creditData = await backendClient.get(`/api/meal-plan-limit/check-replacements${queryParams}`);
    const replacementsLeft = creditData.replacementsRemaining !== undefined ? creditData.replacementsRemaining : 0;

    if (!creditData.isPremium && replacementsLeft < 1) {
      console.log("🚫 Not enough replacements for replace action");
      throw new NoTriesRemainingError('No replacements remaining');
    }
  } catch (error) {
    if (error instanceof NoTriesRemainingError || error instanceof InsufficientCreditsError) {
      throw error;
    }
    console.error("Error checking replacements:", error);
    throw new InsufficientCreditsError('Unable to verify replacements');
  }

  // Consume 1 try BEFORE making the AI request (to prevent abuse)
  const authToken = localStorage.getItem("authToken");
  const incrementBody = {};

  if (!authToken) {
    // For anonymous users, send fingerprint
    const fingerprint = getFingerprint();
    if (fingerprint) {
      incrementBody.fingerprint = fingerprint;
    }
  }

  try {
    console.log("[AlternativeRecipes] Consuming replacement...");
    await backendClient.post(
      "/api/meal-plan-limit/consume-replacement",
      incrementBody,
      { timeout: 5000 }
    );
    console.log("[AlternativeRecipes] ✅ Replacement consumed");
    window.dispatchEvent(new CustomEvent("replacementConsumed"));
  } catch (error) {
    console.warn("[AlternativeRecipes] Consume replacement failed:", error.message);

    // 429 = Too Many Requests (no replacements remaining)
    if (
      error.status === 429 ||
      (error.status === 403 &&
        (error.data?.error?.toLowerCase().includes("replacement") ||
         error.data?.error?.toLowerCase().includes("limit") ||
         error.data?.code === "NO_REPLACEMENTS_REMAINING"))
    ) {
      console.log("🚫 No replacements remaining");
      throw new NoTriesRemainingError('No replacements remaining');
    }
    // For other errors, continue anyway (don't block user experience)
  }
};

/**
 * Generates alternative recipe suggestions using PromptBuilder
 * Uses the same logic as promptGenerators.js via shared PromptBuilder class
 * 
 * @param {Object} dislikedRecipe - The recipe being replaced
 * @param {number|Object} budget - User's budget
 * @param {number} numberOfPeople - Number of people to cook for
 * @param {string|Array} dietaryPreferences - User's dietary preferences
 * @param {Array} leftovers - Available leftover ingredients
 * @param {Function} addReasoningStep - Callback to add reasoning steps
 * @param {string} userCountry - User's country for localized suggestions
 * @param {string} selectedCuisine - User's selected cuisine preference
 * @param {Array} currentMealPlan - Current meal plan to avoid duplicates
 * @param {Object} cookingStyle - Cooking style settings { breakfast: { cookingTime, cookFor2Days }, ... }
 * @param {number} minStorageDays - Minimum shelf life for the slot (3, 4, or 5). Derived from position in meal plan.
 */
export const generateAlternativeRecipesAsync = async (
  dislikedRecipe,
  budget,
  numberOfPeople,
  dietaryPreferences,
  leftovers,
  addReasoningStep,
  userCountry = 'USA',
  selectedCuisine = 'Surprise Me',
  currentMealPlan = [],
  cookingStyle = null,
  minStorageDays = 3
) => {
  addReasoningStep(`Generating alternatives for ${dislikedRecipe.title}...`);
  
  const mealType = dislikedRecipe.mealType || 'Dinner';
  // Use the slot-correct storage target (3/4/5 based on meal position in the plan),
  // not the disliked recipe's own maxStorageTime which belongs to a different recipe.
  const targetStorageDays = minStorageDays;
  
  // Get budget tier
  const budgetTier = typeof budget === 'object' && budget?.key 
    ? budget.key 
    : 'standard';
  
  // Create PromptBuilder instance (unified logic for constraints, hints, inspirations, time limits)
  const promptBuilder = new PromptBuilder({
    country: userCountry,
    selectedCuisine: selectedCuisine,
    dietaryPreferences: dietaryPreferences,
    budgetTier: budgetTier,
    numberOfPeople: numberOfPeople,
    totalMeals: 3, // For alternative recipes, we generate 3 alternatives
    cookingStyle: cookingStyle // Pass cooking style for dynamic time limits
  });
  
  // Get all data from PromptBuilder
const { constraintBlock: cuisineConstraintBlock, hardAvoid, allowedTitleNativeWords = [] } =
    promptBuilder.getAlternativeCuisineConstraint(mealType);

  const titleNativeException = allowedTitleNativeWords.length
    ? ` EXCEPTION: only these canonical ${(selectedCuisine || '').trim()} dish words ARE allowed verbatim in titles: ${allowedTitleNativeWords.slice(0, 6).join(', ')}. The demonym/adjective form of the cuisine (e.g. "${(selectedCuisine || '').trim()}", "Greek", "Italian", "French", "Mediterranean") is STILL FORBIDDEN in titles and descriptions — write "Tortilla Francesa with Parsley" not "Spanish Omelette with Parsley"; write "Yogurt Bowl with Honey" not "Greek Yogurt Bowl with Honey".`
    : '';
  
  const ingredientHints = promptBuilder.getIngredientHints();
  const inspirations = promptBuilder.getInspirations(mealType, { 
    excludeRecipe: dislikedRecipe.title, 
    count: 4 
  });
  const supermarketName = promptBuilder.getSupermarketName();
  const { measurementUnits } = promptBuilder.getMeasurementSystem();
  const dietaryConstraintBlock = promptBuilder.getDietaryConstraintBlock(supermarketName);
  
  // Get dynamic cooking time limit and complexity description based on meal type and cooking style
  const cookingTimeLimit = promptBuilder.getCookingTimeLimit(mealType);
  const complexityDescription = promptBuilder.getComplexityDescription(mealType);
  const isForTwoDays = promptBuilder.isForTwoDays(mealType);
  
  // maxStorageTime floor: use slot-correct target (3/4/5), raised to 2 if batch-cooking
  const maxStorageTime = String(isForTwoDays && targetStorageDays < 2 ? 2 : targetStorageDays);
  
  // Get titles of current meals to exclude (avoid duplicates across ALL meal types)
  const existingMealTitles = currentMealPlan
    .filter(meal => meal.title !== dislikedRecipe.title)
    .map(meal => meal.title.toLowerCase());

  // Include historically-seen titles for this slot so re-generating after multiple
  // replacements doesn't loop back to recipes the user has already rejected.
  const slotKey = getCacheKey(dislikedRecipe, currentMealPlan);
  const seenTitles = slotHistory.get(slotKey) || new Set();
  const allExcludedTitles = [...new Set([
    dislikedRecipe.title.toLowerCase(),
    ...existingMealTitles,
    ...seenTitles,
  ])];

  // Ingredients from the other meals of the same type — used to enforce diversity
  // so alternatives don't repeat a protein/base already dominant in the other 2 meals.
  const siblingMeals = currentMealPlan.filter(
    r => r.mealType === mealType && r.title !== dislikedRecipe.title
  );
  const siblingIngredients = siblingMeals
    .flatMap(r => (r.ingredients || []).slice(0, 3).map(i => i.product))
    .filter(Boolean)
    .slice(0, 6);

  // Sort leftovers by quantity descending and assign at most 1 per alternative.
  // Alternative 3 is always leftover-free to guarantee at least 1 fully fresh option.
  // Listing all leftovers in a shared block causes the AI to anchor all 3 alternatives
  // on the same dominant ingredient (e.g. chicken) — per-slot assignment prevents that.
  const sortedLeftovers = (leftovers || [])
    .map(item => ({
      name: item.productName || item.name || String(item),
      amount: item.leftoverAmount || '',
      qty: parseFloat(item.leftoverAmount) || 0,
    }))
    .filter(item => item.name)
    .sort((a, b) => b.qty - a.qty);

  const formatLeftover = (item) =>
    item.amount ? `${item.name} (${item.amount} remaining)` : item.name;

  const leftoverForAlt1 = sortedLeftovers[0] ? formatLeftover(sortedLeftovers[0]) : null;
  const leftoverForAlt2 = sortedLeftovers[1] ? formatLeftover(sortedLeftovers[1]) : null;
  const hasAnyLeftover = leftoverForAlt1 || leftoverForAlt2;
  
  // Build the enhanced prompt using PromptBuilder data
  const currentLang = getCurrentLanguage();
  const LANG_NAMES = { 
    es: 'Spanish (Español)', 
    ru: 'Russian (Русский)',
    ca: 'Catalan (Català)',
    fr: 'French (Français)',
    ro: 'Romanian (Română)',
    it: 'Italian (Italiano)',
    uk: 'Ukrainian (Українська)'
  };
  const isNonEnglish = currentLang !== 'en' && LANG_NAMES[currentLang];
  const languageInstruction = isNonEnglish
    ? `\n\nLANGUAGE (CRITICAL — MUST FOLLOW):\nWrite "title" and "description" fields ONLY in ${LANG_NAMES[currentLang]}. Do NOT write them in English.\nIngredient names ("product") stay in English.`
    : '';

  const prompt = `
JSON only. Generate exactly 3 alternative ${mealType.toLowerCase()} recipes to replace "${dislikedRecipe.title}".${languageInstruction}

CONTEXT:
- Country: ${userCountry}
- Supermarket: ${supermarketName}
- Number of people: ${numberOfPeople}
- Meal type: ${mealType}
- Must be DIFFERENT from: ${allExcludedTitles.slice(0, 12).join(', ')}
${isForTwoDays ? `- **CRITICAL: This meal is for 2 DAYS. DOUBLE all ingredient quantities.**` : ''}
${cuisineConstraintBlock}
${dietaryConstraintBlock}

MEAL TYPE APPROPRIATENESS (CRITICAL):
- Every recipe MUST be a typical ${mealType.toLowerCase()} dish. ${mealType === 'Breakfast' ? 'No heavy proteins like steak, salmon fillets, or large meat portions — use eggs, dairy, cereals, bread, fruits, light proteins instead.' : mealType === 'Lunch' ? 'Balanced meals — salads, sandwiches, bowls, soups, grain or rice dishes, moderate protein portions.' : 'Full dinner-appropriate dishes — proteins, hearty vegetables, grains, sauces.'}
- If a leftover ingredient does NOT fit ${mealType.toLowerCase()} (e.g. salmon steak for breakfast), do NOT use it — skip it.

DIVERSITY (CRITICAL):
- Each of the 3 alternatives MUST have a DIFFERENT main protein/base ingredient. Do NOT repeat the same dominant ingredient across alternatives.${siblingIngredients.length > 0 ? `\n- The other ${mealType.toLowerCase()} meals this week already use: ${siblingIngredients.join(', ')}. Do NOT make these the main protein/base of any alternative — pick something different.` : ''}
- Vary cooking methods across the 3 recipes (e.g. one baked, one pan-fried, one no-cook/assembled).
- Aim for distinctly different flavor profiles so the user has a real choice.

INSPIRATIONS (derive from these, adapt as needed):
${inspirations.slice(0, 4).map((insp, i) => `${i + 1}. ${insp}`).join('\n')}

${ingredientHints ? `SUGGESTED INGREDIENTS (${budgetTier} tier): ${ingredientHints}` : ''}
${hasAnyLeftover ? `LEFTOVER ASSIGNMENT (optional — one leftover per alternative, as a supporting ingredient only, NOT the main concept):
- Alternative 1: ${leftoverForAlt1 ? `may incorporate ${leftoverForAlt1} if it fits the meal type naturally` : 'no assigned leftover — choose freely'}
- Alternative 2: ${leftoverForAlt2 ? `may incorporate ${leftoverForAlt2} if it fits the meal type naturally` : 'no assigned leftover — choose freely'}
- Alternative 3: NO leftovers — propose something completely fresh and independent
Do NOT make the assigned leftover the main protein or carb base of the alternative. It is an optional supporting element.` : ''}

PER-RECIPE REQUIREMENTS:
- title: string, <=30 chars, attractive, descriptive. MUST NOT include: countries, cuisines, cities, places, chef names, NOR their demonym/adjective forms (no "Spanish", "Greek", "Italian", "French", "Mediterranean", "Asian"). MUST NOT pair the dish with a beverage (no "with coffee", "with café con leche", "with tea", "with juice"). Use flavor/ingredient descriptors instead.${titleNativeException}
- description: string, <=120 chars, enticing summary of flavors, key ingredients, and cooking method. MUST NOT include: countries, cities, places, chef names, their demonym/adjective forms, or beverage pairings.
- ingredients: [{ product, qty, form }] (form = one of ${PRODUCT_FORM_VALUES.join(' | ')}; be accurate — canned/jarred/dried/frozen must say so, so cooking steps never treat a can as fresh) - enough for ${numberOfPeople} person(s)${isForTwoDays ? ' x 2 days (DOUBLED)' : ''}
- cookingTime: number only (no "min"), must be ≤${cookingTimeLimit} - realistic TOTAL time incl. unattended oven/simmer; keep ACTIVE hands-on work light and prefer ONE mostly-passive method (sheet-pan/oven-bake/one-pot) over pre-frying or pre-boiling then baking${complexityDescription ? `. Complexity for this ${mealType.toLowerCase()}: ${complexityDescription}` : ''}
- mealType: "${mealType}"
- maxStorageTime: integer days as a string (e.g. "4"), computed from EACH alternative's actual ingredients (minimum shelf life among them). MUST be ≥ ${maxStorageTime}. Do NOT copy the disliked recipe's value blindly — recompute per alternative.

INGREDIENT QUALITY (CRITICAL):
- ${getNoBeveragesRule(promptBuilder.restrictionKeys)}
- ${getSupermarketReadyRule(promptBuilder.restrictionKeys)}
- ${getIngredientCompletenessRule(promptBuilder.restrictionKeys)}
- ${getProportionalityRuleAlternatives()}
- ${getCookingComplexityRuleAlternatives(promptBuilder.restrictionKeys)}

INGREDIENT NAMING (CRITICAL - ${supermarketName} products):
- Use ${measurementUnits} for quantities
${getAlternativeNamingHint()}
${hardAvoid.length > 0 ? `- DO NOT USE: ${hardAvoid.slice(0, 8).join(', ')}` : ''}

${getIngredientQualitySelfCheckCompact(promptBuilder.restrictionKeys)}

OUTPUT FORMAT:${isNonEnglish ? `
IMPORTANT: "title" and "description" MUST be in ${LANG_NAMES[currentLang]}, NOT in English.` : ''}
{
  "alternatives": [
    {
      "title": "${isNonEnglish ? `Title in ${LANG_NAMES[currentLang]}` : 'Recipe Title'}",
      "description": "${isNonEnglish ? `Description in ${LANG_NAMES[currentLang]}` : 'Brief appetizing description'}",
      "ingredients": [{"product": "whole milk", "qty": "250ml"}, {"product": "chicken breast fillets", "qty": "400g"}],
      "cookingTime": "25",
      "maxStorageTime": "${maxStorageTime}",
      "mealType": "${mealType}"
    }
  ]
}
`;
  
  // Verify the user has a replacement try and consume one before the AI request.
  await checkAndConsumeReplacementTry();

  console.log("[AlternativeRecipes] Processing replacement request");

  // === DEBUG: Init replace debug with compact data ===
  window.__REPLACE_DEBUG = {
    dislikedRecipe: dislikedRecipe.title,
    mealType,
    leftoversCount: leftovers?.length || 0,
    leftoverNames: (leftovers || []).map(l => l.productName || l.name || 'unknown'),
    timestamp: new Date().toISOString(),
  };
  // === END DEBUG ===

  try {
    const response = await fetchAlternativeRecipes(prompt);
    let alternatives = response.alternatives || [];
    
    // Ensure mealType is set correctly and filter out duplicates
    alternatives = alternatives
      .map(alt => ({
        ...alt,
        mealType: mealType, // Ensure correct meal type
        cookingTime: alt.cookingTime || (mealType === 'Breakfast' ? '15' : mealType === 'Lunch' ? '25' : '45')
      }))
      .filter(alt => !allExcludedTitles.includes(alt.title.toLowerCase()));
    
    if (alternatives.length < 3) {
      // Generate fallback alternatives using inspirations
      const fallbackInspirations = inspirations.filter(
        insp => !alternatives.some(alt => insp.toLowerCase().includes(alt.title.toLowerCase().split(' ')[0]))
      );
      
      const fallbackAlternatives = fallbackInspirations.slice(0, 3 - alternatives.length).map((insp, index) => ({
        title: insp.length > 45 ? insp.substring(0, 42) + '...' : insp,
        description: `A delicious ${mealType.toLowerCase()} inspired by ${selectedCuisine === 'Surprise Me' ? 'local' : selectedCuisine} cuisine.`,
        ingredients: [],
        cookingTime: mealType === 'Breakfast' ? '15' : mealType === 'Lunch' ? '25' : '45',
        maxStorageTime: maxStorageTime,
        mealType: mealType
      }));
      
      alternatives = [...alternatives, ...fallbackAlternatives];
    }
    
    addReasoningStep(`Found ${alternatives.length} alternative recipes`);

    // === DEBUG: Store alternatives result (titles only) ===
    if (window.__REPLACE_DEBUG) {
      window.__REPLACE_DEBUG.alternativesTitles = alternatives.slice(0, 3).map(a => a.title);
    }
    // === END DEBUG ===

    return alternatives.slice(0, 3);
  } catch (error) {
    console.error('Error generating alternatives:', error);
    addReasoningStep(`Error generating alternatives: ${error.message}`);
    
    // Use inspirations for fallback
    const fallbackAlternatives = inspirations.slice(0, 3).map(insp => ({
      title: insp.length > 45 ? insp.substring(0, 42) + '...' : insp,
      description: `A delicious ${mealType.toLowerCase()} option that fits your preferences.`,
      ingredients: [],
      cookingTime: mealType === 'Breakfast' ? '15' : mealType === 'Lunch' ? '25' : '45',
      maxStorageTime: maxStorageTime,
      mealType: mealType
    }));
    
    // If no inspirations available, create generic fallbacks
    if (fallbackAlternatives.length < 3) {
      return [
        {
          title: `Simple ${mealType}`,
          description: `A simpler alternative that's quick and easy to prepare.`,
          ingredients: [],
          cookingTime: mealType === 'Breakfast' ? '15' : mealType === 'Lunch' ? '25' : '35',
          maxStorageTime: maxStorageTime,
          mealType: mealType
        },
        {
          title: `Light ${mealType} Option`,
          description: `A lighter take on ${mealType.toLowerCase()} that fits your preferences.`,
          ingredients: [],
          cookingTime: mealType === 'Breakfast' ? '10' : mealType === 'Lunch' ? '20' : '40',
          maxStorageTime: maxStorageTime,
          mealType: mealType
        },
        {
          title: `Quick ${mealType} Dish`,
          description: `A quick and satisfying ${mealType.toLowerCase()} option.`,
          ingredients: [],
          cookingTime: mealType === 'Breakfast' ? '12' : mealType === 'Lunch' ? '22' : '30',
          maxStorageTime: maxStorageTime,
          mealType: mealType
        }
      ];
    }
    
    return fallbackAlternatives;
  }
};

/**
 * Generate the shopping ingredients for a user-typed custom meal.
 *
 * The custom-meal tab in RecipeAlternativesDrawer emits a meal with the user's
 * title but `ingredients: []`. Without ingredients the grocery regen adds no
 * products, storage, or leftovers for that meal. This expands the title into
 * real supermarket ingredients (with quantities) so the replacement flow can
 * treat a custom meal exactly like a suggested alternative.
 *
 * Unlike generateAlternativeRecipesAsync this does NOT consume a replacement try
 * — the try was already consumed when the alternatives drawer was opened.
 *
 * @param {Object} customMeal - { title, mealType, ... }
 * @param {Object} opts - numberOfPeople, dietaryPreferences, userCountry,
 *   selectedCuisine, cookingStyle, budget, minStorageDays, addReasoningStep
 * @returns {Promise<{ingredients: Array<{product,qty}>, cookingTime: string|null, maxStorageTime: string} | null>}
 */
export const generateCustomMealRecipeAsync = async (
  customMeal,
  {
    numberOfPeople = 1,
    dietaryPreferences = [],
    userCountry = 'USA',
    selectedCuisine = 'Surprise Me',
    cookingStyle = null,
    budget = 'standard',
    minStorageDays = 3,
    addReasoningStep = () => {},
  } = {}
) => {
  const title = (customMeal?.title || '').trim();
  if (!title) return null;

  const mealType = customMeal?.mealType || 'Dinner';
  const budgetTier = typeof budget === 'object' && budget?.key ? budget.key : (budget || 'standard');

  // Reuse the same constraint/naming/units logic as alternatives generation so
  // custom ingredients match the store, units, and dietary rules consistently.
  const promptBuilder = new PromptBuilder({
    country: userCountry,
    selectedCuisine,
    dietaryPreferences,
    budgetTier,
    numberOfPeople,
    totalMeals: 1,
    cookingStyle,
  });

  const supermarketName = promptBuilder.getSupermarketName();
  const { measurementUnits } = promptBuilder.getMeasurementSystem();
  const dietaryConstraintBlock = promptBuilder.getDietaryConstraintBlock(supermarketName);
  const cookingTimeLimit = promptBuilder.getCookingTimeLimit(mealType);
  const ingredientHints = promptBuilder.getIngredientHints();
  const isForTwoDays = promptBuilder.isForTwoDays(mealType);
  // Slot-correct storage floor (3/4/5), raised to 2 if batch-cooking.
  const maxStorageTime = String(isForTwoDays && minStorageDays < 2 ? 2 : minStorageDays);

  const prompt = `
JSON only. The user wants to cook this exact ${mealType.toLowerCase()} dish: "${title}". Generate the shopping ingredients for it.
Keep the title EXACTLY as given — do NOT rename the dish.

CONTEXT:
- Country: ${userCountry}
- Supermarket: ${supermarketName}
- Number of people: ${numberOfPeople}
- Meal type: ${mealType}
${isForTwoDays ? `- **CRITICAL: This meal is for 2 DAYS. DOUBLE all ingredient quantities.**` : ''}
${dietaryConstraintBlock}

REQUIREMENTS:
- ingredients: [{ product, qty, form }] (form = one of ${PRODUCT_FORM_VALUES.join(' | ')}; canned/jarred/dried/frozen must be accurate) — everything needed to cook "${title}", enough for ${numberOfPeople} person(s)${isForTwoDays ? ' x 2 days (DOUBLED)' : ''}. Ingredient "product" names stay in English.
- Use ${measurementUnits} for quantities.
- cookingTime: number only (no "min"), must be ≤${cookingTimeLimit} — realistic TOTAL time incl. unattended oven/simmer; keep active work light and prefer one mostly-passive method over pre-frying then baking.
- maxStorageTime: integer days as a string, the minimum shelf life among the actual ingredients. MUST be ≥ ${maxStorageTime}.
- mealType: "${mealType}"
${ingredientHints ? `\nSUGGESTED INGREDIENTS (${budgetTier} tier): ${ingredientHints}` : ''}

INGREDIENT QUALITY (CRITICAL):
- ${getNoBeveragesRule(promptBuilder.restrictionKeys)}
- ${getSupermarketReadyRule(promptBuilder.restrictionKeys)}
- ${getIngredientCompletenessRule(promptBuilder.restrictionKeys)}

INGREDIENT NAMING (CRITICAL - ${supermarketName} products):
${getAlternativeNamingHint()}

${getIngredientQualitySelfCheckCompact(promptBuilder.restrictionKeys)}

OUTPUT FORMAT:
{
  "alternatives": [
    {
      "title": "${title}",
      "ingredients": [{"product": "whole milk", "qty": "250ml"}],
      "cookingTime": "25",
      "maxStorageTime": "${maxStorageTime}",
      "mealType": "${mealType}"
    }
  ]
}
`;

  try {
    addReasoningStep(`Creating your custom meal "${title}"...`);
    const response = await fetchAlternativeRecipes(prompt);
    const recipe = response?.alternatives?.[0] || null;
    if (!recipe || !Array.isArray(recipe.ingredients) || recipe.ingredients.length === 0) {
      console.warn('[AlternativeRecipes] Custom meal generation returned no ingredients for', title);
      return null;
    }
    return {
      ingredients: recipe.ingredients,
      cookingTime: recipe.cookingTime || null,
      maxStorageTime: recipe.maxStorageTime || maxStorageTime,
    };
  } catch (error) {
    console.error('[AlternativeRecipes] Custom meal generation failed:', error);
    return null;
  }
};

/**
 * Handles removing a recipe from the meal plan.
 */
export const handleRemoveRecipeAsync = async (
  recipeToRemove,
  currentMealPlan,
  setMealPlan,
  generateGroceryList,
  addReasoningStep,
  setAlternativesDrawerOpen,
  setIsGeneratingAlternatives,
  setDislikedRecipeState,
  currentProductList = null,
  userCountry = null,
  numberOfPeople = null,
  budget = null
) => {
  if (!recipeToRemove) return;
  
  try {
    setAlternativesDrawerOpen(false);
    setIsGeneratingAlternatives(true);
    addReasoningStep(`Removing ${recipeToRemove.title} from meal plan...`);
    
    console.log('🔍 Debug: Recipe removal started', {
      recipeToRemove: recipeToRemove.title,
      currentMealPlanLength: currentMealPlan.length,
      hasProductList: !!currentProductList,
      hasUserCountry: !!userCountry,
      hasNumberOfPeople: !!numberOfPeople
    });
    
    let filteredMealPlan;
    setMealPlan(prevMealPlan => {
      filteredMealPlan = prevMealPlan.filter(r => r.title !== recipeToRemove.title);
      console.log('🔍 Debug: Filtered meal plan', {
        originalLength: prevMealPlan.length,
        filteredLength: filteredMealPlan.length,
        removedRecipe: recipeToRemove.title
      });
      return filteredMealPlan;
    });

    // Use smart recalculation if we have the required context
    if (currentProductList && userCountry && numberOfPeople && filteredMealPlan) {
      console.log('🎯 Debug: Using smart recalculation path', {
        productListLength: currentProductList.length,
        country: userCountry,
        numberOfPeople,
        filteredMealPlanLength: filteredMealPlan.length
      });
      addReasoningStep(`Using smart grocery list recalculation...`);
      
      const context = {
        country: userCountry,
        numberOfPeople,
        budget,
        onProgress: (progress) => {
          addReasoningStep(`Processing: ${progress.message} (${progress.progress || 0}%)`);
        }
      };

      try {
        const result = await smartRecalculateGroceryList(
          recipeToRemove,
          filteredMealPlan,
          currentProductList,
          context
        );
        
        console.log('🎯 Debug: Smart recalculation result', result);
        
        if (result && result.products) {
          addReasoningStep(`Smart recalculation successful! Updated grocery list.`);
          // Note: We don't update the state here as smartRecalculateGroceryList should handle state updates
          // or we need access to the state setters. For now, fall back to regular generation.
          await generateGroceryList(filteredMealPlan);
        } else {
          // If smart recalculation fails, fall back to regular generation
          addReasoningStep(`Smart recalculation returned no result, falling back to standard generation...`);
          await generateGroceryList(filteredMealPlan);
        }
      } catch (error) {
        console.error('🚨 Debug: Smart recalculation error', error);
        addReasoningStep(`Smart recalculation failed: ${error.message}, falling back to standard generation...`);
        await generateGroceryList(filteredMealPlan);
      }
    } else {
      console.log('🔄 Debug: Using fallback recalculation path', {
        hasProductList: !!currentProductList,
        hasUserCountry: !!userCountry,
        hasNumberOfPeople: !!numberOfPeople,
        hasFilteredMealPlan: !!filteredMealPlan
      });
      // Fall back to regular grocery list generation
      addReasoningStep(`Falling back to standard grocery list recalculation...`);
      if (filteredMealPlan) {
        await generateGroceryList(filteredMealPlan);
      } else {
        // Fallback: calculate filtered list directly if state update isn't synchronous
        const mealPlanAfterRemoval = currentMealPlan.filter(r => r.title !== recipeToRemove.title);
        await generateGroceryList(mealPlanAfterRemoval);
      }
    }
    
    addReasoningStep(`Successfully removed ${recipeToRemove.title} from meal plan`);
  } catch (error) {
    console.error('Error removing recipe:', error);
    addReasoningStep(`Error removing recipe: ${error.message}`);
  } finally {
    setIsGeneratingAlternatives(false);
    setDislikedRecipeState(null);
  }
};

/**
 * Orchestrates the flow when a user dislikes a recipe.
 * Opens the drawer immediately and fetches alternatives in background.
 * Uses cached alternatives if available, filtering out the current recipe.
 * 
 * @param {Object} recipe - The recipe being disliked
 * @param {number|Object} budget - User's budget
 * @param {number} numberOfPeople - Number of people
 * @param {string|Array} dietaryPreferences - Dietary preferences
 * @param {Array} leftovers - Available leftover ingredients
 * @param {Function} setDislikedRecipeState - State setter for disliked recipe
 * @param {Function} setAlternativeRecipes - State setter for alternatives
 * @param {Function} setAlternativesDrawerOpen - State setter for drawer
 * @param {Function} setIsGeneratingAlternatives - State setter for loading
 * @param {Function} addReasoningStep - Callback for reasoning steps
 * @param {string} userCountry - User's country for localized suggestions
 * @param {string} selectedCuisine - User's selected cuisine preference
 * @param {Array} currentMealPlan - Current meal plan to avoid duplicates
 * @param {Object} cookingStyle - User's cooking style preferences
 */
export const handleDislikeRecipeFlow = async (
  recipe,
  budget,
  numberOfPeople,
  dietaryPreferences,
  leftovers,
  setDislikedRecipeState,
  setAlternativeRecipes,
  setAlternativesDrawerOpen,
  setIsGeneratingAlternatives,
  addReasoningStep,
  userCountry = 'USA',
  selectedCuisine = 'Surprise Me',
  currentMealPlan = [],
  cookingStyle = null
) => {
  if (recipe.isPlaceholder) return;
  
  // Check if we have cached alternatives for this slot
  const cachedAlternatives = getCachedAlternatives(recipe, currentMealPlan);
  
  if (cachedAlternatives && cachedAlternatives.length > 0) {
    // Use cached alternatives (already filtered to exclude current recipe)
    setDislikedRecipeState(recipe);
    setAlternativeRecipes(cachedAlternatives);
    setAlternativesDrawerOpen(true);
    setIsGeneratingAlternatives(false);
    addReasoningStep(`Using cached alternatives for ${recipe.title}`);
    return;
  }
  
  // Check if this slot can have alternatives (limit of 2 slots for free plan)
  const canHaveAlternatives = canGetAlternatives(recipe, currentMealPlan);
  
  if (!canHaveAlternatives) {
    // Limit reached - only show remove option (no alternatives)
    setDislikedRecipeState(recipe);
    setAlternativeRecipes([]); // No alternatives available
    setAlternativesDrawerOpen(true);
    setIsGeneratingAlternatives(false);
    addReasoningStep(`Alternative limit reached - only remove option available for ${recipe.title}`);
    return;
  }
  
  // No cache - generate new alternatives
  // Set the disliked recipe and open drawer immediately (with loading state)
  setDislikedRecipeState(recipe);
  setAlternativeRecipes([]); // Clear previous alternatives
  setAlternativesDrawerOpen(true); // Open drawer immediately to show loading
  setIsGeneratingAlternatives(true);
  
  try {
    const alternatives = await generateAlternativeRecipesAsync(
      recipe,
      budget,
      numberOfPeople,
      dietaryPreferences,
      leftovers,
      addReasoningStep,
      userCountry,
      selectedCuisine,
      currentMealPlan,
      cookingStyle
    );
    
    // Cache the alternatives for this slot
    cacheAlternatives(recipe, alternatives, currentMealPlan);
    
    setAlternativeRecipes(alternatives);
  } catch (error) {
    console.error('Error in handleDislikeRecipeFlow generating alternatives:', error);
    addReasoningStep(`Error in dislike flow: ${error.message}`);
    setAlternativeRecipes([]); // Clear alternatives on error - user can still remove
  } finally {
    setIsGeneratingAlternatives(false);
  }
};
